import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Upload, Download, FileUp, FileDown, Loader2, CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const modules = [
  { id: "risks", name: "Risks", importable: true },
  { id: "vendors", name: "Vendors", importable: true },
  { id: "incidents", name: "Incidents", importable: true },
  { id: "threat_intel", name: "Threat Intel", importable: true },
  { id: "controls", name: "Controls", importable: false },
  { id: "documents", name: "Documents", importable: false },
  { id: "soa", name: "SoA Entries", importable: false },
  { id: "audits", name: "Audits", importable: false },
  { id: "corrective_actions", name: "Corrective Actions", importable: false },
  { id: "trainings", name: "Trainings", importable: false },
  { id: "maintenance_tasks", name: "Maintenance Tasks", importable: false },
];

const BulkImportExport = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [exportModule, setExportModule] = useState("risks");
  const [exportFormat, setExportFormat] = useState("json");
  const [importModule, setImportModule] = useState("risks");
  const [importData, setImportData] = useState("");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/bulk/export/${exportModule}?format=${exportFormat}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: exportFormat === "csv" ? "blob" : "json"
      });
      if (exportFormat === "csv") {
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a"); a.href = url;
        a.download = `${exportModule}_export.csv`; a.click(); URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `${exportModule}_export.json`; a.click(); URL.revokeObjectURL(url);
      }
      toast.success(`Exported ${exportModule}`);
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) { toast.error("Paste JSON data"); return; }
    setImporting(true);
    try {
      let parsed;
      try { parsed = JSON.parse(importData); if (!Array.isArray(parsed)) parsed = [parsed]; } catch { toast.error("Invalid JSON"); setImporting(false); return; }
      const res = await axios.post(`${API}/bulk/import/${importModule}`, parsed, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported ${res.data.imported} item(s)`);
      setImportData("");
    } catch (error) {
      toast.error(extractError(error, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="bulk-page">
      <PageHeader
        title={t("bulk.title")}
        description={t("bulk.subtitle")}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border" data-testid="export-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><FileDown className="w-5 h-5 text-blue-500" /> Export Data</CardTitle>
            <CardDescription>Download module data as JSON or CSV</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Module</Label>
                <Select value={exportModule} onValueChange={setExportModule}>
                  <SelectTrigger data-testid="export-module-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{modules.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="json">JSON</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleExport} disabled={exporting} className="w-full" data-testid="export-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export {modules.find(m => m.id === exportModule)?.name}
            </Button>
          </CardContent>
        </Card>
        <Card className="border-border" data-testid="import-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><FileUp className="w-5 h-5 text-green-500" /> Import Data</CardTitle>
            <CardDescription>Import data from JSON (risks, vendors, incidents, threat_intel)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Module</Label>
              <Select value={importModule} onValueChange={setImportModule}>
                <SelectTrigger data-testid="import-module-select"><SelectValue /></SelectTrigger>
                <SelectContent>{modules.filter(m => m.importable).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>JSON Data</Label>
              <Textarea value={importData} onChange={(e) => setImportData(e.target.value)}
                placeholder='[{"title":"Risk 1","asset_id":"asset_1","likelihood":4,"impact":3}]'
                rows={6} className="font-mono text-xs" data-testid="import-data-input" />
            </div>
            <Button onClick={handleImport} disabled={importing} className="w-full" data-testid="import-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Data
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BulkImportExport;
